import { Component, computed, input } from '@angular/core';
import {
  CostForecastComponent,
  PriceForecastComponent,
  PriceTableView,
  toCostComponent,
  toPriceComponent,
} from './definitions';
import { TranslocoPipe } from '@jsverse/transloco';
import { Badge, TooltipDirective } from 'excellence-ui';
import { DatePipe } from '@angular/common';
import { SupplierContractPriceCurrencyUnitPipe } from '../../pipes/supplier-contract-price-currency-unit-pipe';
import { SupplierContractPriceReferenceUnitPipe } from '../../pipes/supplier-contract-price-reference-unit-pipe';
import { SupplierContractPriceRangePipe } from '../../pipes/supplier-contract-price-range-pipe';
import { SupplierContractData } from '../../stores/supplier-contracts.store';
import {
  CostForecastResponse,
  PriceForecastDetailsResponse,
} from '../../../../core/clients/omni-core/models';

@Component({
  selector: 'app-supplier-contract-price-table',
  templateUrl: './supplier-contract-price-table.html',
  imports: [
    TranslocoPipe,
    Badge,
    DatePipe,
    SupplierContractPriceCurrencyUnitPipe,
    SupplierContractPriceReferenceUnitPipe,
    SupplierContractPriceRangePipe,
    TooltipDirective,
  ],
})
export class SupplierContractPriceTable {
  organizationID = input.required<string>();
  supplierContractID = input.required<string>();

  supplierContract = input.required<SupplierContractData | null | undefined>();

  view = input.required<PriceTableView>();

  year = input.required<string>();
  energyInterfaceID = input.required<'ALL' | string>();

  price = input.required<PriceForecastDetailsResponse | null | undefined>();
  cost = input.required<CostForecastResponse | null | undefined>();

  protected priceComponents = computed<
    { titleKey: string; priceComponents: PriceForecastComponent[] }[]
  >(() => {
    const priceListByOrigin = this.price()?.priceListByOrigin ?? null;
    if (!priceListByOrigin) {
      return [];
    }
    return [
      {
        titleKey: 'cockpit.supplierContracts.details.price.category.SUPPLY',
        priceComponents: (priceListByOrigin['SUPPLY'] ?? []).map(toPriceComponent),
      },
      {
        titleKey: 'cockpit.supplierContracts.details.price.category.TAXES',
        priceComponents: (priceListByOrigin['TAXES'] ?? []).map(toPriceComponent),
      },
      {
        titleKey: 'cockpit.supplierContracts.details.price.category.GRID',
        priceComponents: (priceListByOrigin['GRID'] ?? []).map(toPriceComponent),
      },
      {
        titleKey: 'cockpit.supplierContracts.details.price.category.METERING',
        priceComponents: (priceListByOrigin['METERING'] ?? []).map(toPriceComponent),
      },
    ];
  });

  protected costComponents = computed<
    { titleKey: string; priceComponents: CostForecastComponent[] }[]
  >(() => {
    const costForecast = this.cost()?.costForecastListByOrigin ?? null;
    if (!costForecast) {
      return [];
    }

    return [
      {
        titleKey: 'cockpit.supplierContracts.details.price.category.SUPPLY',
        priceComponents: (costForecast['SUPPLY'] ?? []).map(toCostComponent),
      },
      {
        titleKey: 'cockpit.supplierContracts.details.price.category.TAXES',
        priceComponents: (costForecast['TAXES'] ?? []).map(toCostComponent),
      },
      {
        titleKey: 'cockpit.supplierContracts.details.price.category.GRID',
        priceComponents: (costForecast['GRID'] ?? []).map(toCostComponent),
      },
      {
        titleKey: 'cockpit.supplierContracts.details.price.category.METERING',
        priceComponents: (costForecast['METERING'] ?? []).map(toCostComponent),
      },
    ];
  });

  protected components = computed(() => {
    const view = this.view();
    if (view === 'PRICE') {
      return this.priceComponents();
    } else {
      return this.costComponents();
    }
  });

  protected energyPrice = computed(
    () => this.price()?.totalPriceWithoutTaxes ?? { min: 0, max: 0 },
  );
  protected energyPriceWithTaxes = computed(() => this.price()?.totalPrice ?? { min: 0, max: 0 });
  protected energyCost = computed(
    () => this.cost()?.totalEnergyCostWithoutTaxes ?? { min: 0, max: 0 },
  );
  protected energyCostWithTaxes = computed(
    () => this.cost()?.totalEnergyCost ?? { min: 0, max: 0 },
  );
}
